/**
 * useFileUpload Hook
 * Provides file upload functionality with progress tracking and error handling
 * Automatically processes files after upload
 */

import { useCallback, useState } from 'react';
import {
  type FileUploadRequest,
  type FileUploadResponse,
  processFile,
  uploadFile,
} from '@/api/file-upload-api';

export interface UseFileUploadOptions {
  onSuccess?: (response: FileUploadResponse) => void;
  onError?: (error: Error) => void;
  autoProcess?: boolean; // Default: true - automatically process after upload
}

/** How `upload` finished. Callers must branch on `status`; a stored file is not success when processing failed. */
export type FileUploadStatus = 'upload_failed' | 'processing_failed' | 'processed' | 'uploaded';

export interface FileUploadResult {
  status: FileUploadStatus;
  /** The stored file. Null only when the upload itself failed. */
  response: FileUploadResponse | null;
  error: Error | null;
}

export interface UseFileUploadReturn {
  upload: (request: FileUploadRequest) => Promise<FileUploadResult>;
  isUploading: boolean;
  isProcessing: boolean;
  /** Set when the upload request fails. Null when the file was stored. */
  error: Error | null;
  /** Set when the file was stored but automatic processing failed. */
  processingError: Error | null;
  progress: number;
  reset: () => void;
}

const toError = (err: unknown, fallback: string): Error =>
  err instanceof Error ? err : new Error(fallback);

export function useFileUpload(options?: UseFileUploadOptions): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [processingError, setProcessingError] = useState<Error | null>(null);
  const [progress, setProgress] = useState(0);

  const upload = useCallback(
    async (request: FileUploadRequest): Promise<FileUploadResult> => {
      const shouldAutoProcess = options?.autoProcess !== false; // Default to true

      setIsUploading(true);
      setError(null);
      setProcessingError(null);
      setProgress(0);

      try {
        // Step 1: Upload file
        setProgress(25);
        const uploadResponse = await uploadFile(request);
        setProgress(50);
        setIsUploading(false);

        if (!shouldAutoProcess) {
          setProgress(100);
          options?.onSuccess?.(uploadResponse);
          return { status: 'uploaded', response: uploadResponse, error: null };
        }

        setIsProcessing(true);
        setProgress(60);

        try {
          const processResponse = await processFile(uploadResponse.file.file_id);
          setProgress(100);
          setIsProcessing(false);

          const combinedResponse: FileUploadResponse = {
            ...uploadResponse,
            processed: processResponse.processed,
            processing_result: processResponse.processing_result,
          };

          if (!processResponse.processed) {
            const processError = new Error('File processing did not complete');
            setProcessingError(processError);
            return { status: 'processing_failed', response: combinedResponse, error: processError };
          }

          options?.onSuccess?.(combinedResponse);
          return { status: 'processed', response: combinedResponse, error: null };
        } catch (err) {
          // The file is already stored. Do not report that as a successful upload.
          const processError = toError(err, 'File processing failed');
          setIsProcessing(false);
          setProgress(100);
          setProcessingError(processError);
          return {
            status: 'processing_failed',
            response: { ...uploadResponse, processed: false },
            error: processError,
          };
        }
      } catch (err) {
        const uploadError = toError(err, 'Upload failed');
        setError(uploadError);
        setProgress(0);
        setIsUploading(false);
        setIsProcessing(false);
        options?.onError?.(uploadError);
        return { status: 'upload_failed', response: null, error: uploadError };
      }
    },
    [options]
  );

  const reset = useCallback(() => {
    setIsUploading(false);
    setIsProcessing(false);
    setError(null);
    setProcessingError(null);
    setProgress(0);
  }, []);

  return {
    upload,
    isUploading,
    isProcessing,
    error,
    processingError,
    progress,
    reset,
  };
}
